import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { AuthUserContext } from '../Session';
import FirebaseContext from '../Firebase/context';
import SignOutButton from '../SignOut';
import Notifications from '../Notifications/Notifications';
import NotificationToast from '../Notifications/NotificationToast';
import { useNotifications } from '../../hooks/useNotifications';
import * as ROUTES from '../../constants/routes';
import * as ROLES from '../../constants/roles';
import { useProjectSelection } from '../ProjectSelection';
import AddProjectModal from '../VControl/AddProjectModal';

const APPS_LIST = [
  'REPLAN',
  'VCONTROL',
  'SHAPELIST',
  //'CALCULATOR', "HOME", "INVENTORY", "GANTT_PLANNING",  "BLOG"
];

const BRAND_MENU_ITEMS = [...APPS_LIST];

const ThemeToggle = () => {
  const [theme, setTheme] = useState(() => {
    // Get initial theme from localStorage or default to 'dark'
    return localStorage.getItem('theme') || 'dark';
  });

  useEffect(() => {
    // Update the data-theme attribute on the html element
    document.documentElement.setAttribute('data-theme', theme);
    // Save to localStorage
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme((prevTheme) =>
      prevTheme === 'dark' ? 'light' : 'dark',
    );
  };

  return (
    <button
      onClick={toggleTheme}
      className="theme-button"
      aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
    >
      {theme === 'dark' ? (
        // Sun icon for light mode
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="5" />
          <line x1="12" y1="1" x2="12" y2="3" />
          <line x1="12" y1="21" x2="12" y2="23" />
          <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
          <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
          <line x1="1" y1="12" x2="3" y2="12" />
          <line x1="21" y1="12" x2="23" y2="12" />
          <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
          <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
        </svg>
      ) : (
        // Moon icon for dark mode
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
        </svg>
      )}
    </button>
  );
};

const NotificationsButton = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [panelCheckTime, setPanelCheckTime] = useState(0);
  const [initialView, setInitialView] = useState(null);
  const notificationsRef = useRef(null);

  const {
    lastCheckTime,
    unreadCount,
    unreadMessages,
    unreadLogs,
    toasts,
    dismissToast,
    markRead,
    soundEnabled,
    toastsEnabled,
    setSoundEnabled,
    setToastsEnabled,
  } = useNotifications();

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event) => {
      if (
        notificationsRef.current &&
        !notificationsRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const handleOpen = () => {
    setPanelCheckTime(lastCheckTime);
    setIsOpen(true);
    markRead();
  };

  const handleClose = () => {
    setIsOpen(false);
    setInitialView(null);
  };

  const handleToggle = () => (isOpen ? handleClose() : handleOpen());

  const handleToastClick = (toast) => {
    setPanelCheckTime(lastCheckTime);
    setInitialView(toast.type === 'log' ? 'logs' : 'messages');
    setIsOpen(true);
    markRead();
  };

  return (
    <>
      <div ref={notificationsRef} className="notifications-dropdown">
        <button
          className="notifications-button"
          aria-label={t('navigation.notifications')}
          aria-expanded={isOpen}
          onClick={handleToggle}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
            <path d="M13.73 21a2 2 0 0 1-3.46 0" />
          </svg>

          {unreadCount > 0 && (
            <span
              className="notifications-badge"
              aria-label={`${unreadCount} unread notifications`}
            />
          )}
        </button>

        {isOpen && (
          <div className="notifications-dropdown__popover">
            <Notifications
              lastCheckTime={panelCheckTime}
              unreadMessages={unreadMessages}
              unreadLogs={unreadLogs}
              onClose={handleClose}
              initialView={initialView}
              soundEnabled={soundEnabled}
              toastsEnabled={toastsEnabled}
              onSoundEnabledChange={setSoundEnabled}
              onToastsEnabledChange={setToastsEnabled}
            />
          </div>
        )}
      </div>

      {!isOpen &&
        toasts.length > 0 &&
        createPortal(
          <div className="notification-toasts">
            {toasts.map((toast) => (
              <NotificationToast
                key={toast.id}
                toast={toast}
                onDismiss={() => dismissToast(toast.id)}
                onClick={handleToastClick}
              />
            ))}
          </div>,
          document.body,
        )}
    </>
  );
};

const LanguageSelector = () => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const selectorRef = useRef(null);

  const languages = [
    { code: 'en', label: 'EN', full: 'English', emoji: '🇺🇸' },
    { code: 'es', label: 'ES', full: 'Español', emoji: '🇪🇸' },
    { code: 'fr', label: 'FR', full: 'Français', emoji: '🇫🇷' },
  ];

  const currentLang =
    languages.find((lang) => lang.code === i18n.language) ||
    languages[0];

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event) => {
      if (
        selectorRef.current &&
        !selectorRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const changeLanguage = (langCode) => {
    i18n.changeLanguage(langCode);
    setIsOpen(false);
  };

  const { t } = useTranslation();

  return (
    <div ref={selectorRef} className="language-selector">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="language-selector__button"
        aria-label={t('navigation.select_language')}
        aria-expanded={isOpen}
      >
        {/* {currentLang.emoji} */}
        {currentLang.label}
      </button>

      {isOpen && (
        <div className="language-selector__dropdown">
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => changeLanguage(lang.code)}
              className={`language-selector__option ${i18n.language === lang.code
                ? 'language-selector__option--active'
                : ''
                }`}
            >
              <div className="language-selector__option-name">
                {lang.emoji} {lang.full}
              </div>
              {/* <div className='language-selector__option-code'>
                {lang.label}
              </div> */}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const ProjectSelector = ({ hideControls }) => {
  const { t } = useTranslation();
  const {
    projects,
    projectsMeta,
    selectedProject,
    setSelectedProject,
    loadingProjects,
    refreshProjects,
  } = useProjectSelection();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const selectorRef = useRef(null);
  const listRef = useRef(null);
  const searchRef = useRef(null);
  const authUser = useContext(AuthUserContext);
  const firebase = useContext(FirebaseContext);

  const filteredProjects = useMemo(
    () =>
      projects.filter((project) =>
        project.toLowerCase().includes(searchTerm.toLowerCase()),
      ),
    [projects, searchTerm],
  );

  // Reset highlight when search term or open state changes
  useEffect(() => {
    setHighlightedIndex(-1);
  }, [searchTerm, isOpen]);

  useEffect(() => {
    if (hideControls || loadingProjects) return;

    const handleKeyDown = (event) => {
      if (event.altKey && event.key === 'Enter') {
        event.preventDefault();
        setIsOpen((prev) => !prev);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [hideControls, loadingProjects]);

  useEffect(() => {
    if (isOpen && searchRef.current) {
      searchRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const item = listRef.current.children[highlightedIndex];
      if (item) {
        item.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIndex]);

  const handleSelectProject = useCallback(
    (project) => {
      setSelectedProject(project);
      setIsOpen(false);
      setSearchTerm('');
    },
    [setSelectedProject],
  );

  const organisation = useMemo(() => {
    if (!authUser?.roles) return null;
    const orgs = Object.keys(authUser.roles).filter((k) => authUser.roles[k] !== null);
    return orgs.length > 0 ? orgs[0] : null;
  }, [authUser]);

  const handleOpenAddModal = useCallback(() => {
    setIsOpen(false);
    setIsAddModalOpen(true);
  }, []);

  const handleCloseAddModal = useCallback(() => {
    setIsAddModalOpen(false);
  }, []);

  const handleAddProject = useCallback(
    async (
      newProjectName,
      authUserData,
      _editMode,
      _initialProjectName,
      customFields = {},
      _fieldsToDelete = [],
      projectFolders = [],
      organisation = '',
    ) => {
      if (!firebase || !organisation) {
        throw new Error('Firebase or organization not available');
      }

      const projectData = {
        createdBy:
          authUserData?.username ||
          authUserData?.uid ||
          'Unknown User',
        projectFolders,
        ...customFields,
      };

      await firebase.doCreateProject(
        organisation,
        newProjectName,
        projectData,
        authUserData?.uid || null,
        authUserData?.email || null,
      );

      setSelectedProject(newProjectName);
      refreshProjects();
      setIsAddModalOpen(false);
    },
    [firebase, setSelectedProject, refreshProjects],
  );

  // Keyboard navigation within dropdown
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event) => {
      if (
        selectorRef.current &&
        !selectorRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        return;
      }

      if (filteredProjects.length === 0) return;

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setHighlightedIndex((prev) =>
          prev < filteredProjects.length - 1 ? prev + 1 : 0,
        );
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        setHighlightedIndex((prev) =>
          prev > 0 ? prev - 1 : filteredProjects.length - 1,
        );
      } else if (event.key === 'Enter' && highlightedIndex >= 0) {
        event.preventDefault();
        handleSelectProject(filteredProjects[highlightedIndex]);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, filteredProjects, highlightedIndex, handleSelectProject]);

  if (hideControls) return null;

  return (
    <div ref={selectorRef} className="project-selector-container">
      <button
        onClick={() => setIsOpen(!isOpen)}
        data-tt={`${t('navigation.project_desc', { defaultValue: 'Select the active project' })}\n${selectedProject || t('navigation.project', { defaultValue: 'Project' })}\n${t('common.shortcut', { key: 'Alt+Enter', defaultValue: 'Alt+Enter' })}`}
        className="project-selector-button"
        aria-label={t('navigation.project', {
          defaultValue: 'Project',
        })}
        disabled={loadingProjects}
        aria-expanded={isOpen}
      >
        {selectedProject ||
          t('navigation.project', { defaultValue: 'Project' })}
        {selectedProject &&
          projectsMeta[selectedProject] &&
          !projectsMeta[selectedProject].hasDocuments && (
            <span className="project-selector-button__no-plans">
              {t('navigation.no_plans', { defaultValue: 'No plans' })}
            </span>
          )}
      </button>

      {isOpen && (
        <div className="project-selector-dropdown">
          <input
            ref={searchRef}
            type="text"
            placeholder={t('navigation.search_project', {
              defaultValue: 'Search project...',
            })}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="project-selector-search"
          />
          <div className="project-selector-list" ref={listRef}>
            {filteredProjects.map((project, index) => {
              const noPlans =
                projectsMeta[project] && !projectsMeta[project].hasDocuments;
              return (
                <button
                  key={project}
                  onClick={() => handleSelectProject(project)}
                  title={
                    noPlans
                      ? t('navigation.no_plans_tooltip', {
                          defaultValue: 'No plans uploaded yet',
                        })
                      : undefined
                  }
                  className={`project-selector-option ${selectedProject === project
                    ? 'project-selector-option--active'
                    : ''
                    }${index === highlightedIndex ? ' project-selector-option--highlighted' : ''}${noPlans ? ' project-selector-option--no-plans' : ''}`}
                >
                  <span className="project-selector-option__name">
                    {project}
                  </span>
                  {noPlans && (
                    <span className="project-selector-option__badge">
                      {t('navigation.no_plans', {
                        defaultValue: 'No plans',
                      })}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          {organisation && firebase && (
            <button
              onClick={handleOpenAddModal}
              className="project-selector-add-button"
            >
              +{' '}
              {t('navigation.add_project', {
                defaultValue: 'Add Project',
              })}
            </button>
          )}
        </div>
      )}
      {organisation && firebase && (
        <AddProjectModal
          isOpen={isAddModalOpen}
          onClose={handleCloseAddModal}
          onAdd={handleAddProject}
          organisation={organisation}
          authUser={authUser}
          firebase={firebase}
        />
      )}
    </div>
  );
};

const Navigation = () => {
  const location = useLocation();
  // Check pathname/search/hash and also the full browser URL (includes host/port)
  const href =
    typeof window !== 'undefined' && window.location
      ? window.location.href
      : '';
  // const hideControls = (location.pathname + location.search + location.hash + href).includes('360');

  const hideControls = false;

  return (
    <AuthUserContext.Consumer>
      {(authUser) =>
        authUser ? (
          <NavigationAuth
            authUser={authUser}
            hideControls={hideControls}
          />
        ) : (
          <NavigationNonAuth hideControls={hideControls} />
        )
      }
    </AuthUserContext.Consumer>
  );
};

const Brand = ({ hideControls, authUser }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const containerRef = useRef(null);

  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (event) => {
      if (!containerRef.current) return;
      if (containerRef.current.contains(event.target)) return;
      setOpen(false);
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.search, location.hash]);

  const routeForName = (name) => ROUTES[name];
  const menuItems = BRAND_MENU_ITEMS.map((name) => ({
    name,
    path: routeForName(name),
  })).filter((item) => typeof item.path === 'string');

  // Helper to map menu item names to translation keys
  const getTranslationKey = (name) => {
    const lowerName = name.toLowerCase();
    // Special mapping for VCONTROL to avoid conflict with vcontrol namespace
    return lowerName === 'vcontrol'
      ? 'navigation.vcontrol_app'
      : `navigation.${lowerName}`;
  };

  const toggleMenu = (event) => {
    if (hideControls) return;
    if (
      event &&
      (event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey)
    )
      return;
    event.preventDefault();
    setOpen((value) => !value);
  };

  const onKeyDown = (event) => {
    if (hideControls) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setOpen((value) => !value);
    }
  };

  const goTo = (path) => {
    navigate(path);
    setOpen(false);
  };

  const hasProfilePhoto = Boolean(
    authUser?.providerData?.[0]?.photoURL,
  );
  const displayName = authUser?.providerData?.[0]?.displayName;
  const email = authUser?.providerData?.[0]?.email;
  const roleList = authUser?.roles
    ? Object.entries(authUser.roles)
      .filter(([, value]) => Boolean(value))
      .map(([key]) => key)
      .join(', ')
    : '';

  return (
    <div ref={containerRef} className="brand-menu">
      <Link
        to={ROUTES.LANDING}
        className="logo"
        aria-haspopup={hideControls ? undefined : 'menu'}
        aria-expanded={hideControls ? undefined : open}
        onClick={toggleMenu}
        onKeyDown={onKeyDown}
      >
        <img src="/logo.svg" alt="RePlan360" />
      </Link>

      {!hideControls && open && (
        <div
          role="menu"
          aria-label="Navigation"
          className="brand-menu__popover"
        >
          {authUser && (
            <>
              <button
                type="button"
                role="menuitem"
                className="brand-menu__profile"
                onClick={() => goTo(ROUTES.ACCOUNT)}
              >
                <div
                  className="brand-menu__avatar"
                  aria-hidden="true"
                >
                  {hasProfilePhoto ? (
                    <img
                      src={authUser.providerData[0].photoURL}
                      alt=""
                    />
                  ) : (
                    <span className="brand-menu__avatar-fallback">
                      {(displayName || email || 'U')
                        .trim()
                        .charAt(0)
                        .toUpperCase()}
                    </span>
                  )}
                </div>

                <div className="brand-menu__profile-text">
                  <div className="brand-menu__profile-name">
                    {displayName || t('navigation.account')}
                  </div>
                  {email && (
                    <div className="brand-menu__profile-meta">
                      {email}
                    </div>
                  )}
                  {roleList && (
                    <div className="brand-menu__profile-meta">
                      {roleList}
                    </div>
                  )}
                </div>
              </button>
              <div className="brand-menu__divider" role="separator" />
            </>
          )}

          {!authUser && (
            <>
              <button
                type="button"
                role="menuitem"
                className="brand-menu__item"
                onClick={() => goTo(ROUTES.SIGN_IN)}
              >
                {t('navigation.sign_in')}
              </button>
              <button
                type="button"
                role="menuitem"
                className="brand-menu__item"
                onClick={() => goTo(ROUTES.SIGN_UP)}
              >
                {t('navigation.sign_up')}
              </button>
              <div className="brand-menu__divider" role="separator" />
            </>
          )}

          {menuItems.map(({ name, path }) => (
            <button
              key={name}
              type="button"
              role="menuitem"
              onClick={() => goTo(path)}
              className="brand-menu__item"
            >
              {t(getTranslationKey(name))}
            </button>
          ))}

          {authUser && (
            <>
              <div className="brand-menu__divider" role="separator" />
              <div
                className="brand-menu__footer"
                onClick={() => setOpen(false)}
              >
                <SignOutButton />
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const NavigationAuth = ({ authUser, hideControls }) => (
  <nav className="main-nav">
    <ul>
      <li>
        <Brand hideControls={hideControls} authUser={authUser} />
      </li>
      <li>
        <ProjectSelector hideControls={hideControls} />
      </li>
      <li className="nav-right">
        <LanguageSelector />
      </li>
      <li>
        <ThemeToggle />
      </li>
      <li>
        <NotificationsButton />
      </li>
    </ul>
  </nav>
);

const NavigationNonAuth = ({ hideControls }) => (
  <nav className="main-nav">
    <ul>
      <li>
        <Brand hideControls={hideControls} />
      </li>
      <li className="nav-right">
        <LanguageSelector />
      </li>
      <li>
        <ThemeToggle />
      </li>
      {/* {hideControls && <li><Link style={{ margin: 0 }} role='button' to={ROUTES.SIGN_IN} >SignIn</Link></li>} */}
    </ul>
  </nav>
);

export default Navigation;
